import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

/**
 * Data hooks for the Database Explorer — a read-only browser over the cinch_ops
 * Postgres. The catalog (schema → tables) loads in one call and drives the
 * left-hand tree; table detail and row previews load lazily per selection.
 */

export interface TableSummary {
  name: string;
  kind: string;
  est_rows: number | null;
  column_count: number;
  comment: string | null;
}

export interface SchemaNode {
  name: string;
  tables: TableSummary[];
}

export interface CatalogResponse {
  schemas: SchemaNode[];
}

export interface ColumnInfo {
  name: string;
  data_type: string;
  nullable: boolean;
  default: string | null;
  is_primary_key: boolean;
  foreign_key: string | null;
  comment: string | null;
  sensitive: boolean;
}

export interface IndexInfo {
  name: string;
  columns: string[];
  unique: boolean;
}

export interface TableDetail {
  name: string;
  kind: string;
  comment: string | null;
  est_rows: number | null;
  columns: ColumnInfo[];
  indexes: IndexInfo[];
}

export interface RowsResponse {
  columns: string[];
  rows: Record<string, unknown>[];
  masked_columns: string[];
  total: number;
  limit: number;
  offset: number;
  order_by: string | null;
  dir: string;
}

export interface RowsQuery {
  limit: number;
  offset: number;
  orderBy: string | null;
  dir: "asc" | "desc";
  search: string;
}

export function useCatalog() {
  return useQuery({
    queryKey: ["db_explorer", "catalog"],
    queryFn: () => apiFetch<CatalogResponse>("/db-explorer/catalog"),
    staleTime: 5 * 60_000,
  });
}

export function useTableDetail(schema: string | null, table: string | null) {
  return useQuery({
    queryKey: ["db_explorer", "table", schema, table],
    queryFn: () =>
      apiFetch<TableDetail>(
        `/db-explorer/tables/${encodeURIComponent(schema!)}/${encodeURIComponent(table!)}`,
      ),
    enabled: schema != null && table != null,
    staleTime: 60_000,
  });
}

export function useTableRows(
  schema: string | null,
  table: string | null,
  query: RowsQuery,
  enabled: boolean,
) {
  const params = new URLSearchParams({
    limit: String(query.limit),
    offset: String(query.offset),
    dir: query.dir,
  });
  if (query.orderBy) params.set("order_by", query.orderBy);
  if (query.search) params.set("search", query.search);

  return useQuery({
    queryKey: ["db_explorer", "rows", schema, table, query],
    queryFn: () =>
      apiFetch<RowsResponse>(
        `/db-explorer/tables/${encodeURIComponent(schema!)}/${encodeURIComponent(
          table!,
        )}/rows?${params.toString()}`,
      ),
    enabled: enabled && schema != null && table != null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
